import { AddSeries } from '../../../add-series';
import useAddSeries from '../../../add-series/hooks/use-add-series';
import { useSeriesManager } from './hooks/use-series-manager';
import {
  FrequencyTypeEnum,
  TokenVolatilityTimeseriesContext,
  VenueTypeEnum,
  VolatilityTypeEnum,
} from '../../../../../providers/token-volatility-timeseries-provider';
import CircularProgress from '@mui/material/CircularProgress';

import './styles.scss';
import { useContext, useMemo, useState } from 'react';
import Input from '../../../../input/input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';

interface AddRealizedSeriesProps {
  seriesId?: string;
  listTokenFrequency?: any[];
  onApply: (options: Record<string, any>) => void;
  seriesExists: (
    token: string,
    value: string,
    type: VolatilityTypeEnum,
    frequency: FrequencyTypeEnum,
    venue: VenueTypeEnum,
  ) => boolean;
}

const AddRealizedSeries = (props: AddRealizedSeriesProps) => {
  const { onApply, seriesExists } = props;

  const { isOpen, openModal, closeModal, addSeriesButtonRef } = useAddSeries();

  const { fetchListTokenFrequency, listTokenFrequency, isListTokenFrequencyFetched } = useContext(
    TokenVolatilityTimeseriesContext,
  );

  const loading = !isListTokenFrequencyFetched;

  const [tokenSearch, setTokenSearch] = useState('');

  const { volatility, setVolatility, volatilitiesOptions, options } = useSeriesManager({
    listTokenFrequency,
    fetchListTokenFrequency,
  });

  const handleSeriesAdded = () => {
    const selectedValues = options.reduce(
      (acc, option) => {
        acc[option.key] = option.value;
        return acc;
      },
      {} as Record<string, any>,
    );

    onApply({ ...selectedValues, ...{ volatility } });

    closeModal();
  };

  const renderRadioGroup = (
    key: string,
    title: string,
    options: any[],
    value: any,
    onChange: (value: any) => void,
  ) => {
    return (
      <AddSeries.RadioGroup
        title={title}
        value={value}
        onChange={onChange}
        options={options}
        className={`group-${key}`}
      />
    );
  };

  const renderMainContent = useMemo(() => {
    if (loading) {
      return (
        <AddSeries.Content classname="main-modal-content">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 120,
            }}
          >
            <CircularProgress />
          </div>
        </AddSeries.Content>
      );
    }
    const renderRealizedOptions = () => {
      return options.map((option) => (
        <AddSeries.RadioGroup
          key={option.label}
          title={option.label}
          value={option.value}
          onChange={option.setValue}
          options={option.options}
          otherOptions={option.otherOptions}
        />
      ));
    };

    return (
      <AddSeries.Content classname="main-modal-content">
        <AddSeries.Title title="Add Realized Series" />
        {renderRadioGroup(
          'volatility',
          'Volatility Type',
          volatilitiesOptions,
          volatility,
          setVolatility,
        )}

        {volatility === VolatilityTypeEnum.REALIZED && renderRealizedOptions()}
        {volatility === VolatilityTypeEnum.IMPLIED && renderRealizedOptions()}
      </AddSeries.Content>
    );
  }, [volatility, setVolatility, options, loading]);

  const renderOtherContent = useMemo(() => {
    const tokenOptions = options.find((option) => option.key === 'token');
    const hasOtherOptions =
      tokenOptions && tokenOptions.otherOptions && tokenOptions.otherOptions.length > 0;
    const isOtherSelected =
      hasOtherOptions &&
      tokenOptions?.otherOptions?.find((option) => option.value === tokenOptions.value);

    if (!hasOtherOptions || !isOtherSelected) {
      return null;
    }

    const filteredOtherOptions = tokenOptions.otherOptions?.filter((option) =>
      option.label.toLowerCase().includes(tokenSearch.toLowerCase()),
    );

    return (
      <AddSeries.Content classname="other-tokens-content">
        <span className="token-selector-label">Select another token</span>
        <Input
          icon={<FontAwesomeIcon icon={faSearch} />}
          placeholder="Search token"
          className="search-input"
          value={tokenSearch}
          onChange={(e) => setTokenSearch(e.target.value)}
        />
        <AddSeries.RadioGroup
          value={tokenOptions.value}
          onChange={tokenOptions.setValue}
          options={filteredOtherOptions || []}
        />
      </AddSeries.Content>
    );
  }, [tokenSearch, options]);

  const isOtherContentVisible = useMemo(() => {
    const tokenOptions = options.find((option) => option.key === 'token');

    const hasOtherOptions =
      tokenOptions && tokenOptions.otherOptions && tokenOptions.otherOptions.length > 0;

    const isOtherSelected =
      hasOtherOptions &&
      tokenOptions?.otherOptions?.find((option) => option.value === tokenOptions.value);

    return hasOtherOptions && isOtherSelected;
  }, [options]);

  const isSeriesAlreadyExists = useMemo(() => {
    const token = options.find((o) => o.key === 'token')?.value;
    const type = volatility;
    const window = options.find((o) => o.key === 'window')?.value;
    const tenor = options.find((o) => o.key === 'tenor')?.value;
    const frequency = options.find((o) => o.key === 'frequency')?.value;

    const venue =
      type === VolatilityTypeEnum.REALIZED
        ? options.find((o) => o.key === 'venue')?.value
        : VenueTypeEnum.DERIBIT;

    const value = type === VolatilityTypeEnum.REALIZED ? window : tenor;

    return seriesExists(
      token,
      value,
      type as VolatilityTypeEnum,
      frequency as FrequencyTypeEnum,
      venue,
    );
  }, [options]);

  return (
    <AddSeries>
      <AddSeries.Button onClick={openModal} label="Add Series" ref={addSeriesButtonRef} />
      <AddSeries.Modal
        isOpen={isOpen}
        setIsOpen={closeModal}
        onClose={closeModal}
        className={classNames('add-realized-series-modal', {
          'with-other': isOtherContentVisible,
        })}
        anchorEl={addSeriesButtonRef.current}
      >
        <AddSeries.Column className="main-modal-column">
          {renderMainContent}
          <AddSeries.ActionButtons
            onCancel={closeModal}
            onApply={handleSeriesAdded}
            disabled={isSeriesAlreadyExists}
          />
        </AddSeries.Column>
        {isOtherContentVisible && (
          <AddSeries.Column className="other-token-column">{renderOtherContent}</AddSeries.Column>
        )}
      </AddSeries.Modal>
    </AddSeries>
  );
};

export default AddRealizedSeries;
